// apiFetch — like fetch(), but attaches the logged-in user's Supabase access
// token as a Bearer header so the backend can verify the caller. For a multi-org
// user it also sends the chosen workspace as `X-Org-Id` (persisted by the org
// switcher in localStorage); the backend honours it if the user is a member, or
// if they hold a Floxir platform role (support/super_admin — see requireUserOrg).
// Use this for every backend call that touches org-scoped data.

import { supabase } from '../supabase';
import { STAFF_ACCESS_BLOCKED_EVENT as BLOCKED_EVENT } from './events';
import { invalidateAll, isDataWrite } from './dataCache';

export const ORG_STORAGE_KEY = 'floxir.orgId';

// Floxir staff: every visit to a customer's data is recorded first, and the backend REFUSES the request
// (503, staff_access_log_failed) when it cannot record it. Customers never see this. The page shows
// a clear message instead of an empty or broken screen.
export const STAFF_ACCESS_BLOCKED_EVENT = BLOCKED_EVENT;
export const STAFF_ACCESS_LOG_FAILED = 'staff_access_log_failed';

/** True when the backend refused a staff request because the access log could not be written. */
export async function isStaffAccessRefusal(res: Response): Promise<boolean> {
  if (res.status !== 503) return false;
  try {
    const body = await res.clone().json();
    return body?.error === STAFF_ACCESS_LOG_FAILED;
  } catch {
    return false;
  }
}

export async function apiFetch(path: string, options: RequestInit = {}): Promise<Response> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const headers = new Headers(options.headers || {});
  if (session?.access_token) {
    headers.set('Authorization', `Bearer ${session.access_token}`);
  }
  try {
    const orgId = localStorage.getItem(ORG_STORAGE_KEY);
    if (orgId) headers.set('X-Org-Id', orgId);
  } catch {
    /* localStorage unavailable — backend falls back to the earliest membership */
  }
  // An action that changes data drops everything the pages remembered (lib/dataCache.js), when it starts and when
  // it ends, so no page ever shows a state this action has just changed.
  const writes = isDataWrite(path, options.method);
  if (writes) invalidateAll();
  let res: Response;
  try {
    res = await fetch(path, { ...options, headers });
  } finally {
    if (writes) invalidateAll();
  }
  if (await isStaffAccessRefusal(res)) {
    let message: string | undefined;
    try { message = (await res.clone().json())?.message; } catch { /* keep the default message */ }
    window.dispatchEvent(new CustomEvent(STAFF_ACCESS_BLOCKED_EVENT, { detail: { message } }));
  }
  return res;
}
